import React, { useState } from 'react';
import { MessageSquare, Check, Lightbulb, Bug, Heart, MoreHorizontal } from 'lucide-react';
import { submitFeedback } from '../api';
import { useOrg } from '../contexts/OrgContext';

const CATEGORIES = [
  { key: 'idea', label: 'Idea', icon: Lightbulb },
  { key: 'bug', label: 'Bug', icon: Bug },
  { key: 'praise', label: 'Praise', icon: Heart },
  { key: 'other', label: 'Other', icon: MoreHorizontal },
];

const MAX = 5000;

export default function Feedback() {
  const { active } = useOrg();
  const orgId = active?.org_id;
  const [category, setCategory] = useState(null);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (!subject.trim() || !message.trim() || !orgId) return;
    setBusy(true); setMsg(null);
    try {
      await submitFeedback(orgId, { subject: subject.trim(), message: message.trim(), category });
      setSent(true);
      setCategory(null); setSubject(''); setMessage('');
    } catch (e) {
      setMsg({ kind: 'err', text: e.response?.data?.detail || 'Could not send your feedback. Please try again.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-fg flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-orange" /> Send Feedback
        </h1>
        <p className="text-fg-muted text-sm mt-1">
          Tell us what's working, what isn't, or what you'd like to see.
          {active?.name ? <> It goes to the admins of <span className="text-fg">{active.name}</span>.</> : " It goes to your organization's admins."}
        </p>
      </div>

      {sent ? (
        <div className="bg-panel border border-panel-border rounded-2xl p-12 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-emerald-500/15 text-emerald-400 mb-4">
            <Check className="w-7 h-7" />
          </div>
          <div className="text-fg font-medium">Thanks — we got it.</div>
          <p className="text-fg-muted text-sm mt-1">Your feedback is on its way to the team.</p>
          <button
            onClick={() => setSent(false)}
            className="mt-5 px-4 py-2 bg-field border border-field-border text-fg rounded-lg text-sm hover:border-orange transition-colors"
          >
            Send more feedback
          </button>
        </div>
      ) : (
        <div className="bg-panel border border-panel-border rounded-2xl p-5 sm:p-6">
          {!orgId && (
            <div className="mb-4 p-3 rounded-lg border text-sm bg-amber-500/10 border-amber-500/25 text-amber-400">
              Join or select an organization to send feedback — it's routed to that org's admins.
            </div>
          )}
          {msg && (
            <div className="mb-4 p-3 rounded-lg border text-sm bg-red-500/10 border-red-500/25 text-red-400">
              {msg.text}
            </div>
          )}

          <label className="block text-xs font-medium text-fg-muted mb-1.5">What kind of feedback?</label>
          <div className="flex flex-wrap gap-2 mb-5">
            {CATEGORIES.map(({ key, label, icon: Icon }) => {
              const on = category === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCategory(on ? null : key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                    on ? 'bg-orange/10 border-orange/40 text-orange'
                       : 'bg-field border-field-border text-fg-muted hover:text-fg hover:border-fg/20'}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              );
            })}
          </div>

          <label className="block text-xs font-medium text-fg-muted mb-1.5">Title</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="A short title"
            maxLength={140}
            className="w-full mb-5 px-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm placeholder-fg-muted/60 focus:border-orange focus:outline-none transition-colors"
          />

          <label className="block text-xs font-medium text-fg-muted mb-1.5">Your feedback</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX))}
            placeholder="What's on your mind?"
            rows={6}
            className="w-full px-3 py-2.5 bg-field border border-field-border rounded-lg text-fg text-sm placeholder-fg-muted/60 focus:border-orange focus:outline-none transition-colors resize-y"
          />

          <div className="flex items-center justify-between mt-4">
            <span className={`text-xs tabular-nums ${message.length > MAX * 0.9 ? 'text-orange' : 'text-fg-muted'}`}>
              {message.length.toLocaleString()}/{MAX.toLocaleString()}
            </span>
            <button
              onClick={send}
              disabled={busy || !subject.trim() || !message.trim() || !orgId}
              className="px-4 py-2 bg-orange hover:bg-orange-hover text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {busy ? 'Sending…' : 'Send feedback'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
